<template>
  <div class="gm-page balance-workbench">
    <div class="gm-topbar workbench-topbar">
      <div class="workbench-topbar__title">
        <div class="gm-eyebrow">Game Master / Balance Tools</div>
        <h1 class="gm-title">Archetype Balance Workbench</h1>
      </div>

      <GradeScale
        :grades="grades"
        :current-grade="grade"
        @select="selectGrade"
      />
    </div>

    <BandRail
      v-if="band"
      :band="band"
      :open-panel="drawer"
      @open="openDrawer"
    />

    <div class="gm-page__body workbench-body">
      <RosterPanel
        :archetypes="roster"
        :selected-ids="selectedIds"
        :max-selection="maxSelection"
        :collapsed="rosterCollapsed"
        @toggle="toggleArchetype"
        @toggle-collapse="rosterCollapsed = !rosterCollapsed"
      />

      <section class="workspace">
        <BuildTabs
          v-if="selectedBuilds.length"
          :builds="selectedBuilds"
          :active-id="activeId"
          :max-selection="maxSelection"
          @select="activeId = $event"
          @close="toggleArchetype"
        />

        <div class="workspace__pane">
          <CompareMatrix
            v-if="activeId === 'compare' && selectedBuilds.length > 1"
            :builds="selectedBuilds"
            :band="band"
            :grade="grade"
            @open="activeId = $event"
            @close="toggleArchetype"
          />

          <BuildDetail
            v-else-if="activeBuild"
            :key="activeBuild.archetype.id"
            :archetype="activeBuild.archetype"
            :profile="activeBuild.profile"
            :band="band"
          />

          <div v-else class="gm-empty workbench-empty">
            <p class="gm-empty__hint">
              Pick an archetype to open its build at grade {{ grade }}. Open several
              and they become tabs, with a Compare tab that lays every measurement
              side by side.
            </p>
          </div>
        </div>
      </section>

      <WorkbenchDrawer
        v-if="drawer === 'findings'"
        title="Findings at this grade"
        @close="drawer = null"
      >
        <ul class="findings">
          <li v-for="finding in band?.findings ?? []" :key="finding">{{ finding }}</li>
        </ul>
      </WorkbenchDrawer>

      <WorkbenchDrawer
        v-else-if="drawer === 'model'"
        title="How these numbers are calculated"
        @close="drawer = null"
      >
        <ModelLegend :coefficients="coefficients" />
      </WorkbenchDrawer>
    </div>

    <div v-if="isLoading" class="loading-overlay">
      <span class="dx-loader" aria-hidden="true"></span>
      <p>Measuring the roster…</p>
    </div>

    <div v-if="error" class="gm-panel error-display">
      <p>{{ error }}</p>
      <button type="button" class="gm-btn gm-btn--danger gm-btn--sm" @click="error = null">
        Dismiss
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import archetypeService from '@/services/ArchetypeService.js';
import BandRail from '@/components/GameMaster/ArchetypeComparison/BandRail.vue';
import BuildDetail from '@/components/GameMaster/ArchetypeComparison/BuildDetail.vue';
import BuildTabs from '@/components/GameMaster/ArchetypeComparison/BuildTabs.vue';
import CompareMatrix from '@/components/GameMaster/ArchetypeComparison/CompareMatrix.vue';
import GradeScale from '@/components/GameMaster/ArchetypeComparison/GradeScale.vue';
import ModelLegend from '@/components/GameMaster/ArchetypeComparison/ModelLegend.vue';
import RosterPanel from '@/components/GameMaster/ArchetypeComparison/RosterPanel.vue';
import WorkbenchDrawer from '@/components/GameMaster/ArchetypeComparison/WorkbenchDrawer.vue';

// Grade 4 is the first at which every archetype has two schools open, so it is
// the earliest grade where the page compares whole designs rather than opening
// moves. It is also the backend's default, and the two must agree.
const DEFAULT_GRADE = 4;

// Builds are tabs rather than side-by-side columns now, so the cap is what the
// Compare matrix stays readable at rather than what fits across the screen.
const maxSelection = 6;

const grades = archetypeService.grades;
const grade = ref(DEFAULT_GRADE);
const roster = ref([]);
const rows = ref([]);
const band = ref(null);
const coefficients = ref([]);
const selectedIds = ref([]);
const activeId = ref(null);
const rosterCollapsed = ref(false);
const drawer = ref(null);
const isLoading = ref(false);
const error = ref(null);

// The table already holds every archetype measured at this grade, so opening a
// build is a lookup rather than another request.
const selectedBuilds = computed(() =>
  selectedIds.value
    .map((id) => rows.value.find((row) => row.archetype.id === id))
    .filter(Boolean),
);

const activeBuild = computed(() =>
  selectedBuilds.value.find((build) => build.archetype.id === activeId.value) ?? null,
);

// A grade change reloads every row, and a tab pointing at a stale row would
// show the old grade's numbers under the new grade's heading.
watch(selectedBuilds, (builds) => {
  if (activeId.value === 'compare') {
    if (builds.length > 1) return;
    activeId.value = builds[0]?.archetype.id ?? null;
    return;
  }
  if (!builds.some((build) => build.archetype.id === activeId.value)) {
    activeId.value = builds[builds.length - 1]?.archetype.id ?? null;
  }
});

async function loadGrade(next) {
  isLoading.value = true;
  try {
    const table = await archetypeService.loadBalanceTable(next);
    band.value = table.band;
    rows.value = table.rows;
    error.value = null;
  } catch (err) {
    console.error('Failed to load the balance table:', err);
    error.value = `Could not measure the roster at grade ${next}.`;
  } finally {
    isLoading.value = false;
  }
}

async function selectGrade(next) {
  if (next === grade.value) return;
  grade.value = next;
  await loadGrade(next);
}

function toggleArchetype(id) {
  if (selectedIds.value.includes(id)) {
    selectedIds.value = selectedIds.value.filter((selected) => selected !== id);
    return;
  }
  if (selectedIds.value.length >= maxSelection) {
    error.value = `Compare at most ${maxSelection} archetypes at once.`;
    return;
  }
  selectedIds.value = [...selectedIds.value, id];
  activeId.value = id;
}

function openDrawer(panel) {
  drawer.value = drawer.value === panel ? null : panel;
}

onMounted(async () => {
  isLoading.value = true;
  try {
    const [rosterData, model] = await Promise.all([
      archetypeService.loadRoster(),
      archetypeService.loadModel(),
    ]);
    roster.value = rosterData;
    coefficients.value = model.coefficients;
  } catch (err) {
    console.error('Failed to load the archetype roster:', err);
    error.value = 'Could not load the archetype roster. Reload to try again.';
  } finally {
    isLoading.value = false;
  }
  await loadGrade(grade.value);
});
</script>

<style scoped>
/* The page is a workbench, not a document: it fills the viewport exactly once
   and every region that can outgrow its space scrolls inside itself. The
   previous version stacked a band panel, a legend panel and a fixed 40rem main
   area down the document, so the window scrolled before a single build was
   open — and scrolling the window took the roster and the grade scale away
   with it. */
/* Absolute inside `main.main-content` (which is position: relative) rather than
   `height: 100%`. The app shell sizes main-content from its content, so a
   percentage height has nothing to resolve against and the page grows to fit
   the roster instead of the roster scrolling inside the page. Taking the
   workbench out of flow inverts that: main-content contributes no height, the
   shell's `min-height: 100vh` decides it, and the workbench fills exactly it. */
.balance-workbench {
  position: absolute;
  inset: 0;
  min-height: 0;
  overflow: hidden;
}

.workbench-topbar {
  flex: 0 0 auto;
  padding: var(--gm-gap-sm) var(--gm-gap-lg);
}

.workbench-topbar__title {
  min-width: 0;
}

.workbench-body {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-sm) var(--gm-gap-sm) var(--gm-dock-clearance);
}

.workspace {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.workspace__pane {
  flex: 1;
  min-height: 0;
  display: flex;
}

.workspace__pane > * {
  flex: 1;
  min-width: 0;
}

.workbench-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gm-gap-lg);
}

.findings {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.findings li {
  font-size: 0.8rem;
  line-height: 1.45;
  color: var(--gm-text-muted);
}

.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 14, 15, 0.85);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-overlay p {
  margin-top: var(--gm-gap);
  color: var(--gm-accent);
}

.error-display {
  position: fixed;
  bottom: calc(var(--gm-dock-clearance) + var(--gm-gap));
  right: var(--gm-gap-lg);
  z-index: 1000;
  max-width: 25rem;
  border-color: rgba(201, 69, 63, 0.4);
}

.error-display p {
  margin: 0 0 var(--gm-gap-sm);
  color: var(--gm-danger);
  font-weight: 500;
}

.error-display button {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .workbench-topbar {
    flex-wrap: wrap;
  }

  .error-display {
    left: var(--gm-gap);
    right: var(--gm-gap);
    max-width: none;
  }
}
</style>
